import { defineStore } from "pinia";
import { ref, watch } from "vue";
import qz from "qz-tray";
import { useToast } from "vue-toastification";
import { KEYUTIL, KJUR } from "jsrsasign";
import { hextob64 } from "jsrsasign/lib/jsrsasign";
import jsPDF from "jspdf";

const toast = useToast();

export const useQzStore = defineStore("qz", () => {
  const allPrinters = ref([]);
  const selectedPrinter = ref(null);
  const useQzTrayPrint = ref(false);
  const isQzConnected = ref(false);
  const isConnecting = ref(false);

  const selectedSizeConfig = ref(null);
  const selectedTrayConfig = ref(null);
  const selectedMarginConfig = ref({ top: 0, right: 0, bottom: 0, left: 0 });
  const selectedScaleConfig = ref(0);

  const selectedBarcodePrinter = ref(null);
  const selectedBarcodeSizeConfig = ref(null);
  const selectedBarcodeTrayConfig = ref(null);
  const selectedBarcodeMarginConfig = ref({ top: 0, right: 0, bottom: 0, left: 0 });
  const selectedBarcodeScaleConfig = ref(0);
  const printWithoutPrice = ref(false);


  function setSelectedPrinterSizeWidthHeightandTray(size, tray) {
    selectedSizeConfig.value = size;
    selectedTrayConfig.value = tray;
  }

  function setSelectedBarcodePrinterSizeWidthHeightandTray (size, tray) {
    selectedBarcodeSizeConfig.value = size;
    selectedBarcodeTrayConfig.value = tray;
  }

  // Load saved printer from localStorage
  const initSelectedPrinter = () => {
    const saved = localStorage.getItem("selectedPrinter");
    if (saved) {
      selectedPrinter.value = { label: saved, value: saved };
    }
  };

  const initPrinterSettings = () => {
    const savedTray = localStorage.getItem("qz_selectedTrayConfig");
    const savedSize = localStorage.getItem("qz_selectedSizeConfig");
    const savedMargins = localStorage.getItem("qz_selectedMarginConfig");
    const savedScale = localStorage.getItem("qz_selectedScaleConfig");

    if (savedTray) {
      try {
        selectedTrayConfig.value = JSON.parse(savedTray);
      } catch (e) {
        console.warn("Failed to parse qz_selectedTrayConfig:", e);
        selectedTrayConfig.value = savedTray;
      }
    }

    if (savedSize) {
      try {
        selectedSizeConfig.value = JSON.parse(savedSize);
      } catch (e) {
        console.warn("Failed to parse qz_selectedSizeConfig:", e);
        selectedSizeConfig.value = savedSize;
      }
    }

    if (savedMargins) {
      try {
        selectedMarginConfig.value = JSON.parse(savedMargins);
      } catch (e) {
        console.warn("Failed to parse qz_selectedMarginConfig:", e);
        // keep existing default if parsing fails
      }
    }

    if (savedScale) {
      try {
        // stored as JSON.stringify so JSON.parse should yield a number or string
        const parsed = JSON.parse(savedScale);
        selectedScaleConfig.value = typeof parsed === "number" ? parsed : Number(parsed);
      } catch (e) {
        console.warn("Failed to parse qz_selectedScaleConfig:", e);
        selectedScaleConfig.value = Number(savedScale);
      }
    }
  }

  // Barcode printer settings initialization

  const initSelectedBarcodePrinter = () => {
    const saved = localStorage.getItem("qz_selectedBarcodePrinter");
    if (saved) {
      try {
        const parsedValue = JSON.parse(saved);
        selectedBarcodePrinter.value = { label: parsedValue, value: parsedValue };
      } catch (e) {
        console.warn("Failed to parse qz_selectedBarcodePrinter:", e);
        // Fallback to using saved value directly if parsing fails
        selectedBarcodePrinter.value = { label: saved, value: saved };
      }
    }
  };

  const initBarcodePrinterSettings = () => {
    
    const savedTray = localStorage.getItem("qz_selectedBarcodeTrayConfig");
    const savedSize = localStorage.getItem("qz_selectedBarcodeSizeConfig");
    const savedMargins = localStorage.getItem("qz_selectedBarcodeMarginConfig");
    const savedScale = localStorage.getItem("qz_selectedBarcodeScaleConfig");
    

    if (savedTray) {
      try {
        selectedBarcodeTrayConfig.value = JSON.parse(savedTray);
      } catch (e) {
        console.warn("Failed to parse qz_selectedBarcodeTrayConfig:", e);
        selectedBarcodeTrayConfig.value = savedTray;
      }
    }

    if (savedSize) {
      try {
        selectedBarcodeSizeConfig.value = JSON.parse(savedSize);
      } catch (e) {
        console.warn("Failed to parse qz_selectedBarcodeSizeConfig:", e);
        selectedBarcodeSizeConfig.value = savedSize;
      }
    }

    if (savedMargins) {
      try {
        selectedBarcodeMarginConfig.value = JSON.parse(savedMargins);
      } catch (e) {
        console.warn("Failed to parse qz_selectedBarcodeMarginConfig:", e);
        // keep existing default if parsing fails
      }
    }

    if (savedScale) {
      try {
        const parsed = JSON.parse(savedScale);
        selectedBarcodeScaleConfig.value = typeof parsed === "number" ? parsed : Number(parsed);
      } catch (e) {
        console.warn("Failed to parse qz_selectedBarcodeScaleConfig:", e);
        selectedBarcodeScaleConfig.value = Number(savedScale);
      }
    }
  };

  //Load saved printer mode from localStorage
  const initPrinterMode = () => {
    const saved = localStorage.getItem("useQzTrayPrint");
    if (saved !== null) {
      useQzTrayPrint.value = JSON.parse(saved);
    }
  };

  //Initialize QZ connection status
  const initQzConnectionStatus = () => {
    isQzConnected.value = qz.websocket.isActive();
  };

  // --- Persist selected printer ---
  watch(selectedPrinter, (newVal) => {
    if (newVal) {
      localStorage.setItem("selectedPrinter", JSON.stringify(newVal.value));
    }
  });

  //Persist printer mode
  watch(useQzTrayPrint, (newVal) => {
    localStorage.setItem("useQzTrayPrint", JSON.stringify(newVal));
  });

  // Watch selected tray, size, margin, and scale changes and save to localStorage
  watch(selectedTrayConfig, (newValue) => {
    localStorage.setItem("qz_selectedTrayConfig", JSON.stringify(newValue));
  }, { deep: true });
  
  watch(selectedSizeConfig, (newValue) => {
    localStorage.setItem("qz_selectedSizeConfig", JSON.stringify(newValue));
  }, { deep: true });
  
  watch(selectedMarginConfig, (newValue) => {
    localStorage.setItem("qz_selectedMarginConfig", JSON.stringify(newValue));
  }, { deep: true });
  
  watch(selectedScaleConfig, (newValue) => {
    localStorage.setItem("qz_selectedScaleConfig", JSON.stringify(newValue));
  });

  // Barcode printer settings watchers

  watch(selectedBarcodePrinter, (newVal) => {
    if (newVal) {
      localStorage.setItem("qz_selectedBarcodePrinter", JSON.stringify(newVal.value));
    }
  });

  watch(selectedBarcodeTrayConfig, (newValue) => {
    localStorage.setItem("qz_selectedBarcodeTrayConfig", JSON.stringify(newValue));
  }, { deep: true });
  
  watch(selectedBarcodeSizeConfig, (newValue) => {
    localStorage.setItem("qz_selectedBarcodeSizeConfig", JSON.stringify(newValue));
  }, { deep: true });
  
  watch(selectedBarcodeMarginConfig, (newValue) => {
    localStorage.setItem("qz_selectedBarcodeMarginConfig", JSON.stringify(newValue));
  }, { deep: true });
  
  watch(selectedBarcodeScaleConfig, (newValue) => {
    localStorage.setItem("qz_selectedBarcodeScaleConfig", JSON.stringify(newValue));
  });


  async function connectQZ(fetchPrinters = true) {
    // Prevent multiple simultaneous connection attempts
    if (isConnecting.value) {
      console.log("QZ connection already in progress, skipping...");
      return;
    }
    
    if (!qz.websocket.isActive()) {
      isConnecting.value = true;
      try {
        await qz.websocket.connect();
        console.log("Qz connected.");
        isQzConnected.value = true;
        // Reset printer mode to false when connection is established
        useQzTrayPrint.value = false;
        if (fetchPrinters) {
          await getAllPrinters();
        }
      } catch (e) {
        console.log("Failed to connect Qz.");
        isQzConnected.value = false;
        console.error(e);
      } finally {
        isConnecting.value = false;
      }
    } else {
      isQzConnected.value = true;
    }
  }

  async function disconnectQZ() {
    // Reset connecting flag when disconnecting
    isConnecting.value = false;
    
    if (qz.websocket.isActive()) {
      try {
        await qz.websocket.disconnect();
        console.log("Qz disconnected.");
        isQzConnected.value = false;
        useQzTrayPrint.value = false;
        allPrinters.value = [];
        selectedPrinter.value = null;
      } catch (e) {
        console.log("Failed to disconnect Qz.");
        console.error(e);
      }
    } else {
      isQzConnected.value = false;
    }
  }

  function initQZSecurity() {
    // Load the certificate
    qz.security.setCertificatePromise((resolve, reject) => {
      fetch("/certs/digital-certificate.txt")
        .then((res) => res.text())
        .then(resolve)
        .catch(reject);
    });

    // Sign messages with private key
    qz.security.setSignaturePromise((toSign) => {
      return (resolve, reject) => {
        fetch("/certs/private-key.txt")
          .then((res) => res.text())
          .then((pem) => {
            try {
              const key = KEYUTIL.getKey(pem);
              const sig = new KJUR.crypto.Signature({ alg: "SHA1withRSA" });
              sig.init(key);
              sig.updateString(toSign);
              const hex = sig.sign(); // hex signature
              const b64 = hextob64(hex); // convert → base64
              resolve(b64); // return base64 string
            } catch (err) {
              reject(err);
            }
          })
          .catch(reject);
      };
    });
  }

  const getAllPrinters = async () => {
    if (!qz.websocket.isActive()) {
      isQzConnected.value = false;
      toast.error(
        "QZ Tray is not connected. Please ensure QZ Tray is running and connected."
      );
      return;
    }

    try {
      const printers = await qz.printers.find();
      allPrinters.value = (Array.isArray(printers) ? printers : [printers])
        .filter(Boolean)
        .map((p) => ({ label: p, value: p })); // normalize format

      console.log("Available Printers:", allPrinters.value);

      if (allPrinters.value.length === 0) {
        toast.info("No printers found.");
        selectedPrinter.value = null;
        return;
      }

      toast.success(`Found ${allPrinters.value.length} printer(s).`);

      const savedPrinter = localStorage.getItem("selectedPrinter");
      if (savedPrinter) {
        // try to match saved printer
        const match = allPrinters.value.find(
          (p) => p.value === JSON.parse(savedPrinter)
        );
        if (match) {
          selectedPrinter.value = match;
        } else {
          // fallback to the first printer if saved printer not found
          selectedPrinter.value = allPrinters.value[0];
        }
      } else {
        // fallback to the first printer
        selectedPrinter.value = allPrinters.value[0];
      }

      // Restore barcode printer selection from localStorage
      const savedBarcodePrinter = localStorage.getItem("qz_selectedBarcodePrinter");
      if (savedBarcodePrinter) {
        // try to match saved barcode printer
        const match = allPrinters.value.find(
          (p) => p.value === JSON.parse(savedBarcodePrinter)
        );
        if (match) {
          selectedBarcodePrinter.value = match;
        }
      }
    } catch (error) {
      console.error("Error fetching printers:", error);
      toast.error("Failed to fetch printers. Please check QZ Tray connection.");
    }
  };

  const printBarcode = async (product) => {
    if (!qz.websocket.isActive()) {
      isQzConnected.value = false;
      toast.error("QZ Tray is not connected. Please ensure QZ Tray is running and connected.");
      return;
    }

    // Use barcode printer if available, otherwise fallback to regular printer
    const printer = selectedBarcodePrinter.value || selectedPrinter.value;
    if (!printer) {
      toast.error("No printer selected. Please select a printer in settings.");
      return;
    }

    try {
      const barcode = product.Barcodes?.[0]?.Barcode;
      if (!barcode) {
        toast.error('No barcode available for this product');
        return;
      }

      const retailSku = product.RetailSku || '';
      const productName = product.ItemName || '';
      
      // Get price from first branch if available
      const price = product.Branches?.[0]?.Price || product.Price || '';
      const formattedPrice = price ? `$${parseFloat(price).toFixed(2)}` : '';

      // Generate barcode SVG
      const canvas = document.createElement('canvas');
      const JsBarcode = (await import('jsbarcode')).default;

      JsBarcode(canvas, barcode, {
        format: "CODE128",
        width: 2,
        height: 60,
        displayValue: false,
        fontSize: 12,
        margin: 5
      });

      const barcodeImage = canvas.toDataURL('image/png');

      // Create HTML template for barcode label
      const printStyles = `
        @page {
          size: 50mm 25mm;
          margin: 0;
        }
        html, body {
          width: 50mm;
          height: 25mm;
          margin: 0;
          padding: 0;
        }
        body {
          box-sizing: border-box;
          font-family: Arial, sans-serif;
          font-size: 3.5mm; /* base font size for scaling */
        }
        .barcode-label {
          width: 50mm;
          height: 25mm;
          padding: 1mm; 
          box-sizing: border-box;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          align-items: stretch;
          gap: 1mm;
        }
        .barcode-info {
          flex: 0 0 auto;
          display: flex;
          flex-direction: column;
          gap: 0.2mm;
        }
        .barcode-info .retail-sku {
          font-weight: bold;
          font-size: 11px;
          margin-bottom: 2px;
        }
        .barcode-info .product-name {
          font-size: 2.6mm;
          line-height: 1;
          max-height: 5.5mm; /* allow up to two small lines */
          overflow: hidden;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          word-wrap: break-word;
        }
        .barcode-center {
          flex: 1 1 auto;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 0.5mm;
        }
        .barcode-image {
          width: 100%;
          max-width: 48mm;
          height: auto;
          max-height: 10mm;
          display: block;
        }
        .barcode {
          font-size: 2.2mm;
          line-height: 1;
          text-align: center;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .price {
          font-weight: 700;
          font-size: 3.2mm;
          text-align: center;
          padding-top: 0.2mm;
        }
      `;

      const labelHtml = `
        <html>
          <head>
            <meta charset="UTF-8">
            <style>${printStyles}</style>
          </head>
          <body>
            <div class="barcode-label">
              <div class="barcode-info">
                ${retailSku ? `<div class="retail-sku">${retailSku}</div>` : ''}
                ${productName ? `<div class="product-name">${productName}</div>` : ''}
              </div>
              <div style="display: flex; flex-direction: column; align-items: center;">
                <img src="${barcodeImage}" alt="Barcode" class="barcode-image" />
                <label class="barcode"> ${barcode} </label>
                ${formattedPrice && printWithoutPrice.value ? `<div class="price">${formattedPrice}</div>` : ''}
              </div>
            </div>
          </body>
        </html>
      `;

      const printData = [
        {
          type: "pixel",
          format: "html",
          flavor: "plain",
          data: labelHtml,
        },
      ];

      // Prepare config options - use barcode printer settings if available, otherwise defaults
      let configOptions = {
        copies: 1,
        rasterize: false,
        scaleContent: true,
        scale: selectedBarcodeScaleConfig.value || 0,
      };

      // Use barcode printer margins if available, otherwise no margins
      if (selectedBarcodeMarginConfig.value && 
          Object.keys(selectedBarcodeMarginConfig.value).length > 0) {
        configOptions.margins = { ...selectedBarcodeMarginConfig.value };
      } else {
        configOptions.margins = { top: 0, right: 0, bottom: 0, left: 0 };
      }

      // Use barcode printer tray if available
      if (selectedBarcodeTrayConfig.value && selectedBarcodeTrayConfig.value.value) {
        configOptions.tray = selectedBarcodeTrayConfig.value.value;
      }

      // Use barcode printer size if available, otherwise use default 50mm x 25mm
      if (selectedBarcodeSizeConfig.value && selectedBarcodeSizeConfig.value.label && barcodePrinterDetails.value) {
        const sizeName = selectedBarcodeSizeConfig.value.label;
        const printerSize = barcodePrinterDetails.value.sizes?.find(s => s.name === sizeName);

        if (printerSize?.in) {
          configOptions.size = {
            width: printerSize.in.width,
            height: printerSize.in.height,
            units: "in",
          };
        } else if (printerSize?.mm) {
          configOptions.size = {
            width: printerSize.mm.width,
            height: printerSize.mm.height,
            units: "mm",
          };
        } else {
          // Size config exists but size not found, use default
          configOptions.size = {
            width: 1.96,
            height: .98,
            units: "in",
          };
        }
      } else {
        // Default label size: 50mm wide x 25mm high
        configOptions.size = {
          width: 1.96,
          height: .98,
          units: "in",
        };
      }

      const config = qz.configs.create(printer.value, configOptions);

      await qz.print(config, printData);

      toast.success(`Barcode printed successfully on ${printer.label}`);
    } catch (error) {
      console.error('Error printing barcode:', error);
      toast.error('Failed to print barcode. Please check printer connection.');
    }
  };

  const printInvoice = async () => {
    if (!qz.websocket.isActive()) {
      isQzConnected.value = false;
      toast.error("QZ Tray is not connected. Please ensure QZ Tray is running and connected.");
      return;
    }

    if (!selectedPrinter.value) {
      toast.error("No printer selected. Please select a printer in settings.");
      return;
    }

    try {
      const invoiceElement = document.querySelector(".invoice-print");
      if (!invoiceElement) {
        toast.error("Invoice content not found. Please ensure the invoice is visible.");
        return;
      }

      // Clone invoice and clean up
      const invoiceClone = invoiceElement.cloneNode(true);
      const printButton = invoiceClone.querySelector(".class-hide-on-print");
      if (printButton) printButton.remove();

      // Collect all styles
      let cssText = "";
      for (const sheet of document.styleSheets) {
        try {
          for (const rule of sheet.cssRules) {
            cssText += rule.cssText + "\n";
          }
        } catch (e) {
          console.warn("Skipping stylesheet due to CORS:", sheet.href);
        }
      }

      const printStyles = `
        @page {
          size: auto;
          margin: 0 !important;
        }

        body {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
          margin: 0 !important;
          padding: 0 !important;
          width: 100%;
        }

        .invoice-print {
          display: block !important;
          width: 100% !important;
          max-width: 100% !important;
          overflow: visible !important;
          margin: 0 !important;
          padding: 8px 16px !important;
          box-shadow: none !important;
          max-height: auto !important;
        }

        table {
          width: 100% !important;
          border-collapse: collapse !important;
          page-break-inside: auto !important;
        }

        tr, td, th {
          page-break-inside: avoid !important;
          break-inside: avoid !important;
          word-wrap: break-word !important;
          white-space: normal !important;
          font-size: 10px !important;
        }

        tr {
          page-break-after: auto !important;
        }

        .sidebar-menu, .checkout-Left, .class-hide-on-print {
          display: none !important;
        }
      `;
      cssText += printStyles;

      const invoiceHtml = `
        <html>
          <head>
            <meta charset="UTF-8">
            <style>${cssText}</style>
          </head>
          <body>
            ${invoiceClone.outerHTML}
          </body>
        </html>
      `;

      const printData = [
        {
          type: "pixel",
          format: "html",
          flavor: "plain",
          data: invoiceHtml,
        },
      ];

      // Use printer’s default settings but allow scaling
      // --- Prepare dynamic config ---
      let configOptions = {
        copies: 1,
        rasterize: false,
        scaleContent: true,
        scale: selectedScaleConfig.value || 0,
        margins: { ...selectedMarginConfig.value },
      };

      if (selectedTrayConfig.value && selectedTrayConfig.value.value) {
        configOptions.tray = selectedTrayConfig.value.value;
      }

      if (selectedSizeConfig.value && selectedSizeConfig.value.label) {
        const sizeName = selectedSizeConfig.value.label;
        const printerSize = printerDetails.value?.sizes?.find(s => s.name === sizeName);

        if (printerSize?.in) {
          configOptions.size = {
            width: printerSize.in.width,
            height: printerSize.in.height,
            units: "in",
          };
        } else if (printerSize?.mm) {
          configOptions.size = {
            width: printerSize.mm.width,
            height: printerSize.mm.height,
            units: "mm",
          };
        }
      }

      const config = qz.configs.create(selectedPrinter.value.value, configOptions);


      await qz.print(config, printData);
      toast.success(`Invoice printed successfully on ${selectedPrinter.value.label}`);
    } catch (error) {
      console.error("Error printing invoice:", error);
      toast.error("Failed to print invoice. Please check printer connection.");
    }
  };

  const printCashDrop = async (data) => {
    if (!qz.websocket.isActive()) {
      isQzConnected.value = false;
      toast.error("QZ Tray is not connected. Please ensure QZ Tray is running and connected.");
      return;
    }

    if (!selectedPrinter.value) {
      toast.error("No printer selected. Please select a printer in settings.");
      return;
    }

    try {
      const { amount, date, closerName, posProfile } = data;

      // Format date: 01-Jan-2026
      const dateObj = new Date(date);
      const day = String(dateObj.getDate()).padStart(2, '0');
      const month = dateObj.toLocaleString('en-US', { month: 'short' });
      const year = dateObj.getFullYear();
      const formattedDate = `${day}-${month}-${year}`;

      // First name only
      const firstName = closerName ? closerName.split(' ')[0] : '';

      const printStyles = `
        @page {
          size: auto;
          margin: 0 !important;
        }

        * {
          box-sizing: border-box;
        }

        body {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
          margin: 0 !important;
          padding: 0 !important;
          width: 100%;
        }

        .cash-drop-container {
          display: block !important;
          width: 100% !important;
          max-width: 100% !important;
          overflow: visible !important;
          margin: 0 !important;
          padding: 8px 16px !important;
          font-family: Arial, sans-serif;
          font-size: 12px;
        }

        .header {
          text-align: center;
          margin-bottom: 20px;
        }

        .header h2 {
          margin: 0;
          font-size: 18px;
          text-transform: uppercase;
        }

        .details {
          width: 100% !important;
          border-collapse: collapse !important;
        }

        .details td {
          padding: 5px 0;
          page-break-inside: avoid !important;
          break-inside: avoid !important;
          word-wrap: break-word !important;
          white-space: normal !important;
        }

        .label {
          font-weight: bold;
          text-align: left;
        }

        .value {
          text-align: right;
        }

        .amount-row td {
          border-top: 1px dashed #000;
          border-bottom: 1px dashed #000;
          padding: 10px 0;
          font-size: 16px;
          font-weight: bold;
        }
      `;

      const htmlContent = `
        <html>
          <head>
            <meta charset="UTF-8">
            <style>${printStyles}</style>
          </head>
          <body>
            <div class="cash-drop-container">
              <div class="header">
              </div>
              <table class="details">
                <tr>
                  <td class="label">Date:</td>
                  <td class="value">${formattedDate}</td>
                </tr>
                <tr>
                  <td class="label">POS Profile:</td>
                  <td class="value">${posProfile || ''}</td>
                </tr>
                <tr>
                  <td class="label">Closer:</td>
                  <td class="value">${firstName}</td>
                </tr>
                <tr class="amount-row">
                  <td class="label">Cash Drop:</td>
                  <td class="value">$${parseFloat(amount).toFixed(2)}</td>
                </tr>
              </table>
            </div>
          </body>
        </html>
      `;

      const printData = [
        {
          type: "pixel",
          format: "html",
          flavor: "plain",
          data: htmlContent,
        },
      ];

      // Configuration options
      let configOptions = {
        copies: 1,
        rasterize: false,
        scaleContent: true,
        scale: selectedScaleConfig.value || 0,
        margins: { ...selectedMarginConfig.value },
      };

      if (selectedTrayConfig.value && selectedTrayConfig.value.value) {
        configOptions.tray = selectedTrayConfig.value.value;
      }

      if (selectedSizeConfig.value && selectedSizeConfig.value.label) {
        const sizeName = selectedSizeConfig.value.label;
        const printerSize = printerDetails.value?.sizes?.find(s => s.name === sizeName);

        if (printerSize?.in) {
          configOptions.size = {
            width: printerSize.in.width,
            height: printerSize.in.height,
            units: "in",
          };
        } else if (printerSize?.mm) {
          configOptions.size = {
            width: printerSize.mm.width,
            height: printerSize.mm.height,
            units: "mm",
          };
        }
      }

      const config = qz.configs.create(selectedPrinter.value.value, configOptions);

      await qz.print(config, printData);
      toast.success(`Cash drop printed successfully on ${selectedPrinter.value.label}`);

    } catch (error) {
      console.error("Error printing cash drop:", error);
      toast.error("Failed to print cash drop.");
    }
  }

  const printerDetails = ref(null);

  const getPrinterDetails = async () => {
    if (!selectedPrinter.value) {
      toast.error("Please select a printer first.");
      return;
    }

    if (!qz.websocket.isActive()) {
      isQzConnected.value = false;
      toast.error(
        "QZ Tray is not connected. Please ensure QZ Tray is running and connected."
      );
      return;
    }

    try {
      // fetch full list of all printer details
      const details = await qz.printers.details();

      // find the matching printer
      const current = details.find(
        (p) => p.name === selectedPrinter.value.value
      );

      if (!current) {
        toast.error("Selected printer details not found.");
        return;
      }

      printerDetails.value = current;

      console.log("Selected Printer Details:", current);
      console.log("Available Paper Sizes:", current.sizes?.map(s => s.name));
      console.log("Available Trays:", current.trays);

      // toast.success(`Fetched details for ${current.name}`);
    } catch (error) {
      console.error("Error fetching printer details:", error);
      toast.error("Failed to fetch printer details.");
    }
  };

    const barcodePrinterDetails = ref(null);

    const getBarcodePrinterDetails = async () => {
      if (!selectedBarcodePrinter.value) {
        toast.error("Please select a barcode printer first.");
        return;
      }

      if (!qz.websocket.isActive()) {
        isQzConnected.value = false;
        toast.error(
          "QZ Tray is not connected. Please ensure QZ Tray is running and connected."
        );
        return;
      }
  
      try {
        // fetch full list of all printer details
        const details = await qz.printers.details();
  
        // find the matching printer
        const current = details.find(
          (p) => p.name === selectedBarcodePrinter.value.value
        );
  
        if (!current) {
          toast.error("Selected barcode printer details not found.");
          return;
        }
  
        barcodePrinterDetails.value = current;
  
        console.log("Selected Barcode Printer Details:", current);
        console.log("Available Paper Sizes:", current.sizes?.map(s => s.name));
        console.log("Available Trays:", current.trays);
  
        // toast.success(`Fetched details for ${current.name}`);
      } catch (error) {
        console.error("Error fetching barcode printer details:", error);
        toast.error("Failed to fetch barcode printer details.");
      }
    };

  return {
    allPrinters,
    selectedPrinter,
    useQzTrayPrint,
    isQzConnected,
    isConnecting,
    connectQZ,
    disconnectQZ,
    initQZSecurity,
    getAllPrinters,
    initSelectedPrinter,
    initPrinterMode,
    initQzConnectionStatus,
    printBarcode,
    printInvoice,
    getPrinterDetails,
    printerDetails,
    setSelectedPrinterSizeWidthHeightandTray,
    selectedSizeConfig,
    selectedTrayConfig,
    // initPrinterSizeAndTray,
    // initPrinterMarginAndScale,
    selectedMarginConfig,
    selectedScaleConfig,
    initPrinterSettings,

    // Barcode printer settings
    selectedBarcodePrinter,
    selectedBarcodeSizeConfig,
    selectedBarcodeTrayConfig,
    selectedBarcodeMarginConfig,
    selectedBarcodeScaleConfig,
    initBarcodePrinterSettings,
    initSelectedBarcodePrinter,
    setSelectedBarcodePrinterSizeWidthHeightandTray,
    getBarcodePrinterDetails,
    barcodePrinterDetails,
    printWithoutPrice,
    printCashDrop,
  };
});
